import { getDisplayNameFromVariable } from './config-reader/variables';
import { hasVariables, isVariableObject } from './config-schemas/has-variables';

const TRIPLET_SEPARATOR = ':';
export const RDK_NAMESPACE = 'rdk';
export const RDK_NAMESPACE_INTERNAL = 'rdk-internal';
export const RDK_API_TYPE_COMPONENT = 'component';
export const RDK_API_TYPE_SERVICE = 'service';
export const RDK_MODEL_FAMILY_BUILTIN = 'builtin';
export const RDK_MODEL_NAME_BUILTIN = 'builtin';
export const RDK_MODEL_NAME_FAKE = 'fake';
export const RDK_MODEL_TRIPLET_BUILTIN = `${RDK_NAMESPACE}${TRIPLET_SEPARATOR}${RDK_MODEL_FAMILY_BUILTIN}${TRIPLET_SEPARATOR}${RDK_MODEL_NAME_BUILTIN}`;
export const CUSTOM_RESOURCE_TYPE = 'custom';

export type ResourceTripletString =
  `${string}${typeof TRIPLET_SEPARATOR}${string}${typeof TRIPLET_SEPARATOR}${string}`;

/** An API triplet broken into its constituent parts. */
export interface APITriplet {
  /** First triplet part, e.g. `rdk`. */
  namespace: string;

  /** Second triplet part, e.g. `component`. */
  type: string;

  /** Third triplet part, e.g. `camera`. */
  subtype: string;

  /** If the API was not specified as a full triplet when read. */
  isBackCompatFormat?: true;
}

/** A model triplet broken into its constituent parts. */
export interface ModelTriplet {
  /** First triplet part, e.g. `rdk`. */
  namespace: string;

  /** Second triplet part, e.g. `builtin`. */
  family: string;

  /** Third triplet part, e.g. `webcam`. */
  name: string;

  /** If the model was not specified as a full triplet when read. */
  isBackCompatFormat?: true;
}

/** Check if a string is a valid API or model triplet. */
export const isResourceTriplet = (
  value: string | undefined
): value is ResourceTripletString => {
  const parts = value?.split(TRIPLET_SEPARATOR);

  return parts?.length === 3 && Boolean(parts[0] && parts[1] && parts[2]);
};

/** Check if a string is an API triplet of a certain resource type. */
export const isAPITriplet = (
  value: string | undefined,
  expectedResourceType: string
): value is ResourceTripletString => {
  return (
    isResourceTriplet(value) &&
    splitAPITriplet(value).type === expectedResourceType
  );
};

/** Given a string, ensure it is a full API triplet. */
export const ensureAPITriplet = (
  apiOrSubtype: string,
  resourceType: string
): ResourceTripletString => {
  return isResourceTriplet(apiOrSubtype)
    ? apiOrSubtype
    : joinResourceTriplet({
        namespace: RDK_NAMESPACE,
        type: resourceType,
        subtype: apiOrSubtype,
      });
};

/** Given a string, ensure it is a full model triplet. */
export const ensureModelTriplet = (
  modelOrName: string | undefined
): ResourceTripletString => {
  if (isResourceTriplet(modelOrName)) {
    return modelOrName;
  }

  const name =
    modelOrName === '' || modelOrName === undefined
      ? RDK_MODEL_NAME_BUILTIN
      : modelOrName;

  return joinResourceTriplet({
    namespace: RDK_NAMESPACE,
    family: RDK_MODEL_FAMILY_BUILTIN,
    name,
  });
};

/**
 * Given an API triple, returns the resource type that should be displayed to the user.
 *
 * ```ts
 * 'rdk:component:base' => 'base'
 * 'foo:bar:baz'        => 'custom'
 * ```
 * @param api A string from the model in the form of a triple.
 * @returns A string, either the resource type or 'custom'.
 */
export const getResourceType = (api: string): string => {
  if (!isResourceTriplet(api)) {
    return CUSTOM_RESOURCE_TYPE;
  }

  const { namespace, subtype } = splitAPITriplet(api);

  if (namespace === RDK_NAMESPACE) {
    return subtype;
  }

  return CUSTOM_RESOURCE_TYPE;
};

/**
 * Given an API string, split it into its constituent parts
 *
 * @param api a colon-delimited api triplet.
 * @returns the triplet, broken apart.
 */
export const splitAPITriplet = (api: ResourceTripletString): APITriplet => {
  // handle if the api is secretly a fragment variable property.
  // this is a hack abusing the underlying types. if a variable is used
  // to define the api or model types in a fragment config, we secretly
  // pass that variable object along and pretend it's an api or model
  // object, then handle that case in specific places relevant to the fragment
  // page only
  if (hasVariables(api)) {
    return api as unknown as APITriplet;
  }

  const [namespace, type, subtype] = splitResourceTriplet(api);

  return { namespace, type, subtype };
};

/**
 * Given a model string, split it into its constituent parts
 *
 * If not a triplet, the model will set to `rdk:builtin:${model}`.
 * If undefined or '', the model will be set to `rdk:builtin:builtin`.
 *
 * @param modelOrName a string or colon-delimited model triplet.
 * @returns the triplet, broken apart.
 */
export const splitModelTriplet = (
  modelOrName: string | undefined
): ModelTriplet => {
  // handle if the model is secretly a fragment variable property.
  // this is a hack abusing the underlying types. if a variable is used
  // to define the api or model types in a fragment config, we secretly
  // pass that variable object along and pretend it's an api or model
  // object, then handle that case in specific places relevant to the fragment
  // page only
  if (hasVariables(modelOrName)) {
    return modelOrName as unknown as ModelTriplet;
  }

  const model = ensureModelTriplet(modelOrName);
  const [namespace, family, name] = splitResourceTriplet(model);

  return modelOrName === model
    ? { namespace, family, name }
    : { namespace, family, name, isBackCompatFormat: true };
};

/**
 * Given split triplet, join it into a fully qualified string.
 *
 * @param triplet the split triplet
 * @returns a colon-delimited triplet string
 */
export const joinResourceTriplet = (
  triplet: APITriplet | ModelTriplet
): ResourceTripletString => {
  // handle if the api or model is secretly a fragment variable property.
  // this is a hack abusing the underlying types. if a variable is used
  // to define the api or model types in a fragment config, we secretly
  // pass that variable object along and pretend it's an api or model
  // object, then handle that case in specific places relevant to the fragment
  // page only
  if (isVariableObject(triplet)) {
    return getDisplayNameFromVariable(triplet) as ResourceTripletString;
  }

  const parts =
    'subtype' in triplet
      ? ([triplet.namespace, triplet.type, triplet.subtype] as const)
      : ([triplet.namespace, triplet.family, triplet.name] as const);

  return `${parts[0]}${TRIPLET_SEPARATOR}${parts[1]}${TRIPLET_SEPARATOR}${parts[2]}`;
};

export const splitResourceTriplet = (
  value: ResourceTripletString
): [string, string, string] => {
  return value.split(TRIPLET_SEPARATOR) as [string, string, string];
};
